import { useCallback, useEffect, useRef, useState } from "react";
import type { PostcodeInputData } from "../types";
import { callTool } from "../api/client";
import { ctaTranslate } from "../lib/ctaTranslate";
import { useLabel } from "../lib/labels";
import {
  POSTCODE_SERVICE_ERROR,
  parseValidatePostcodeResult,
} from "../lib/parseValidatePostcode";
import { Button, TextField } from "../ui";

interface Props {
  data: PostcodeInputData;
  sessionToken: string;
}

const VALIDATE_TOOL = "commerce_validate_postcode";
const POST_CODE_FIELD = "post_code";

export default function PostcodeInputCard({ data, sessionToken }: Props) {
  const submitLabel = useLabel("check", data.submitLabel || "Check");
  const fieldLabel = useLabel("postalCodeLabel", "Postal Code");
  const placeholder = useLabel("placeholder", "Type Postal Code");

  const mountedRef = useRef(true);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [postCode, setPostCode] = useState(
    () => data.fields?.find((field) => field.id === POST_CODE_FIELD)?.value || "",
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearServerError = useCallback((fieldId: string) => {
    setServerErrors((prev) => {
      if (!prev[fieldId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const handleSubmit = async (values: Record<string, string>) => {
    const postCode = (values[POST_CODE_FIELD] || "").trim();
    if (!postCode || submitting) {
      return;
    }
    if (!sessionToken.trim()) {
      console.warn("[PostcodeInputCard] missing sessionToken — cannot validate postcode");
      if (mountedRef.current) {
        setServerErrors({
          [POST_CODE_FIELD]: POSTCODE_SERVICE_ERROR,
        });
      }
      return;
    }

    if (mountedRef.current) {
      setServerErrors({});
      setSubmitting(true);
    }
    try {
      const rpcBody = await callTool(
        VALIDATE_TOOL,
        { post_code: postCode },
        sessionToken,
      );
      if (!mountedRef.current) {
        return;
      }
      const parsed = parseValidatePostcodeResult(rpcBody);
      if (!parsed.ok) {
        setServerErrors({ [POST_CODE_FIELD]: parsed.errorMessage });
        return;
      }
      // BRFrame은 message가 비어 있으면 chat.send_prompt를 버린다.
      await ctaTranslate({
        code: "purchase_clarify.postcode_cta",
        params: { post_code: postCode },
        sessionToken,
        fallback: `Check postal code ${postCode}`,
        toolCall: {
          tool: VALIDATE_TOOL,
          args: { post_code: postCode },
        },
      });
    } catch (err) {
      console.warn("[PostcodeInputCard] postcode validate tool-call failed", err);
      if (mountedRef.current) {
        setServerErrors({ [POST_CODE_FIELD]: POSTCODE_SERVICE_ERROR });
      }
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  };

  return (
    <form
      className="ac-track-form ac-postcode-form"
      autoComplete="off"
      aria-busy={submitting}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit({ [POST_CODE_FIELD]: postCode });
      }}
    >
      <h2 className="ac-track-form__title">{fieldLabel}</h2>
      <div className="ac-track-form__field">
        <TextField
          label={fieldLabel}
          required
          type="text"
          placeholder={placeholder}
          value={postCode}
          status={serverErrors[POST_CODE_FIELD] ? "error" : "default"}
          assistiveText={serverErrors[POST_CODE_FIELD]}
          onChange={(event) => {
            setPostCode(event.target.value);
            clearServerError(POST_CODE_FIELD);
          }}
        />
      </div>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={!postCode.trim() || submitting}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
